import React, { useState, useEffect } from 'react'
import { HiDocumentText, HiShieldCheck, HiDatabase, HiMail, HiChevronDown, HiGlobe } from 'react-icons/hi'
import '../App.css' // Ensure styles are loaded
import './LegalPage.css'

function LegalPage() {
    const [openSection, setOpenSection] = useState('terms') // Default open section
    const [showEmail, setShowEmail] = useState(false)

    // Auto-scroll to expanded section
    useEffect(() => {
        if (openSection) {
            // Small timeout to allow animation to start/layout to update
            const timer = setTimeout(() => {
                const element = document.getElementById(`accordion-${openSection}`)
                if (element) {
                    const yOffset = -100 // Offset for sticky header
                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
                    window.scrollTo({ top: y, behavior: 'smooth' })
                }
            }, 300)
            return () => clearTimeout(timer)
        }
    }, [openSection])

    const toggleSection = (section) => {
        if (openSection === section) {
            setOpenSection(null)
        } else {
            setOpenSection(section)
        }
    }

    const sections = [
        {
            id: 'terms',
            title: 'Terms of Service',
            icon: <HiDocumentText />,
            content: (
                <>
                    <h4>1. Acceptance of Terms</h4>
                    <p>
                        By accessing and using this application, you accept and agree to be bound by the terms and provision of this agreement.
                        The developer of Realtime Polls reserves the right to update these terms at any time.
                    </p>

                    <h4>2. Use License</h4>
                    <p>
                        Permission is granted to temporarily download one copy of the materials (information or software) on
                        Realtime Polls's website for personal, non-commercial transitory viewing only.
                    </p>

                    <h4>3. Disclaimer</h4>
                    <p>
                        The materials on Realtime Polls's website are provided on an 'as is' basis.
                        Realtime Polls makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
                    </p>

                    <h4>4. Limitations</h4>
                    <p>
                        In no event shall Realtime Polls or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Realtime Polls's website.
                    </p>
                </>
            )
        },
        {
            id: 'privacy',
            title: 'Privacy Policy',
            icon: <HiShieldCheck />,
            content: (
                <>
                    <h4>1. Information Collection</h4>
                    <p>
                        We collect information from you when you use our service, such as voting in a poll or creating a new poll.
                        Data collected includes Poll Content, Votes, and technical data necessary for security and operation.
                    </p>

                    <h4>2. Use of Information</h4>
                    <p>
                        Any of the information we collect from you may be used in one of the following ways:
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li>To provide and operate the polling service</li>
                        <li>To improve our website and user experience</li>
                        <li>To monitor the usage of our service via Google Analytics</li>
                        <li>To prevent abuse and fraudulent voting</li>
                    </ul>

                    <h4>3. Data Protection</h4>
                    <p>
                        We implement a variety of security measures to maintain the safety of your personal information when you enter, submit, or access your personal information.
                    </p>
                </>
            )
        },
        {
            id: 'cookies',
            title: 'Cookies & Local Storage',
            icon: <HiGlobe />,
            content: (
                <>
                    <h4>1. Local Storage</h4>
                    <p>
                        We use Local Storage to store data on your device, such as your voting history and preferences. This ensures a smooth user experience and prevents duplicate voting.
                    </p>

                    <h4>2. Google Analytics Cookies</h4>
                    <p>
                        We use Google Analytics to analyze how users use our website. Google Analytics uses cookies to collect information on your activity, such as pages visited and interactions. This data is anonymized and helps us improve our service.
                    </p>
                </>
            )
        },
        {
            id: 'data',
            title: 'Data Collection & Usage',
            icon: <HiDatabase />,
            content: (
                <>
                    <h4>Data Retention</h4>
                    <p>
                        We will retain your information for as long as your account is active or as needed to provide you services. We will retain and use your information as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements.
                    </p>

                    <h4>Third Party Disclosure</h4>
                    <p>
                        We do not sell, trade, or otherwise transfer to outside parties your personally identifiable information. This does not include trusted third parties who assist us in operating our website, conducting our business, or servicing you, so long as those parties agree to keep this information confidential.
                    </p>
                </>
            )
        },
        {
            id: 'contact',
            title: 'Contact Information',
            icon: <HiMail />,
            content: (
                <>
                    <p>
                        If there are any questions regarding this privacy policy or terms of service, you may contact us using the information below:
                    </p>
                    <div className="contact-details">
                        <p>
                            <strong>Email: </strong>
                            {showEmail ? (
                                <a
                                    href="mailto:realtime.polls.official@gmail.com"
                                    className="email-link"
                                    style={{ color: '#3b82f6' }}
                                >
                                    realtime.polls.official@gmail.com
                                </a>
                            ) : (
                                <button
                                    onClick={() => setShowEmail(true)}
                                    className="reveal-email-btn"
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#3b82f6',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                >
                                    [Click to reveal email]
                                </button>
                            )}
                        </p>
                        <p><strong>Address:</strong> Contact via Email</p>
                    </div>
                </>
            )
        }
    ]

    return (
        <div className="legal-container fade-in">
            <div className="legal-header">
                <h1 className="legal-title">Legal & Terms</h1>
                <p className="legal-subtitle">Last Updated: <span>December 24, 2024</span></p>
            </div>

            <div className="accordion-container">
                {sections.map((section, index) => (
                    <div
                        key={section.id}
                        id={`accordion-${section.id}`}
                        className={`accordion-item ${openSection === section.id ? 'active' : ''}`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                    >
                        <button
                            className="accordion-header"
                            onClick={() => toggleSection(section.id)}
                            aria-expanded={openSection === section.id}
                        >
                            <div className="accordion-header-left">
                                <span className="accordion-icon">{section.icon}</span>
                                <h3 className="accordion-title">{section.title}</h3>
                            </div>
                            <HiChevronDown className="accordion-chevron" />
                        </button>
                        <div className="accordion-content">
                            <div className="accordion-body">
                                {section.content}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default LegalPage
